import React, { useState, useEffect } from 'react';
import { Upload, Eye, Trash2, Star, Search, FileText, ExternalLink, Plus } from 'lucide-react';
import { Button } from './ui/button';
import { Card } from './ui/card';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Switch } from './ui/switch';
import { toast } from 'sonner';
import { useTheme } from '@/contexts/ThemeContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CATEGORIES = [
  { id: 'payroll', name: 'Payroll & Timesheets', emoji: '💰' },
  { id: 'worksite', name: 'Work Site Info', emoji: '🏢' },
  { id: 'forms', name: 'Forms & Documents', emoji: '📝' },
  { id: 'program_info', name: 'Program Information', emoji: '📋' },
  { id: 'career', name: 'Career Resources', emoji: '💼' },
  { id: 'faq', name: 'FAQs & Help', emoji: '❓' }
];

const ResourceManagement = () => {
  const { colors } = useTheme();
  const [resources, setResources] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState(null);
  
  const [uploadForm, setUploadForm] = useState({
    title: '',
    description: '',
    url: '',
    category: 'program_info',
    is_featured: false
  });
  const [uploadMode, setUploadMode] = useState('file'); // 'file' or 'link'
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    fetchResources();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCategory]);

  const fetchResources = async () => {
    setLoading(true);
    try {
      let url = `${API_URL}/api/resources`;
      if (selectedCategory) url += `?category=${selectedCategory}`;
      
      const response = await fetch(url, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      
      if (response.ok) setResources(await response.json());
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  const uploadResource = async () => {
    if (!uploadForm.title) { toast.error('Please provide a title'); return; }
    if (uploadMode === 'link' && !uploadForm.url) { toast.error('Please provide a URL'); return; }
    if (uploadMode === 'file' && !selectedFile) { toast.error('Please select a file'); return; }

    setUploading(true);
    try {
      let resourceUrl = uploadForm.url;
      let fileName = '';

      // If file upload, use multipart form data
      if (uploadMode === 'file' && selectedFile) {
        const formData = new FormData();
        formData.append('file', selectedFile);

        const uploadRes = await fetch(`${API_URL}/api/resources/upload-file`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
          body: formData
        });

        if (uploadRes.ok) {
          const uploadData = await uploadRes.json();
          resourceUrl = uploadData.file_url;
          fileName = selectedFile.name;
        } else {
          const errData = await uploadRes.json().catch(() => ({}));
          toast.error(errData.detail || 'File upload failed');
          setUploading(false);
          return;
        }
      }

      const response = await fetch(`${API_URL}/api/resources`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('token')}` },
        body: JSON.stringify({
          title: uploadForm.title,
          description: uploadForm.description,
          url: resourceUrl,
          category: uploadForm.category,
          resource_type: uploadMode === 'file' ? (fileName.endsWith('.pdf') ? 'pdf' : 'document') : 'link',
          is_featured: uploadForm.is_featured,
          file_name: fileName
        })
      });

      if (response.ok) {
        toast.success('Resource added successfully!');
        setShowUploadModal(false);
        setUploadForm({ title: '', description: '', url: '', category: 'program_info', is_featured: false });
        setSelectedFile(null);
        setUploadMode('file');
        fetchResources();
      } else {
        const errData = await response.json().catch(() => ({}));
        toast.error(errData.detail || 'Failed to create resource');
      }
    } catch (error) {
      toast.error('Error uploading');
    }
    setUploading(false);
  };

  const deleteResource = async (id, title) => {
    if (!confirm(`Delete "${title}"?`)) return;
    
    try {
      const res = await fetch(`${API_URL}/api/resources/${id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      if (res.ok) {
        toast.success('Deleted');
        fetchResources();
      }
    } catch (error) {
      toast.error('Failed');
    }
  };

  const filteredResources = resources.filter(r =>
    r.title.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold" style={{ color: colors.text }}>Resource Management</h2>
          <p className="text-sm" style={{ color: colors.text }}>Upload documents for your program</p>
        </div>
        <Button onClick={() => setShowUploadModal(true)} className="bg-blue-600 hover:bg-blue-700 text-white">
          <Plus className="w-4 h-4 mr-2" />Add Resource
        </Button>
      </div>

      <Card className="p-6" style={{ backgroundColor: colors.card, borderColor: colors.border }}>
        <div className="space-y-4">
          {filteredResources.map(r => (
            <div key={r.id} className="flex items-center justify-between p-4 rounded-lg" style={{ backgroundColor: colors.surface }}>
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-1">
                  {r.is_featured && <Star className="w-4 h-4 text-yellow-500" fill="currentColor" />}
                  <p className="font-bold" style={{ color: colors.text }}>{r.title}</p>
                </div>
                <p className="text-sm" style={{ color: colors.text_secondary }}>{r.description}</p>
                <Badge className="mt-2">{CATEGORIES.find(c => c.id === r.category)?.emoji} {r.category}</Badge>
              </div>
              <div className="flex gap-2">
                <Button variant="ghost" size="sm" onClick={() => window.open(r.url, '_blank')} className="text-blue-600">
                  <ExternalLink className="w-4 h-4" />
                </Button>
                <Button variant="ghost" size="sm" onClick={() => deleteResource(r.id, r.title)} className="text-red-500">
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      </Card>

      <Dialog open={showUploadModal} onOpenChange={setShowUploadModal}>
        <DialogContent style={{ backgroundColor: colors.card }}>
          <DialogHeader>
            <DialogTitle style={{ color: colors.text }}>Add Resource</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label style={{ color: colors.text }}>Title *</Label>
              <Input value={uploadForm.title} onChange={(e) => setUploadForm({...uploadForm, title: e.target.value})} placeholder="Document title" style={{ backgroundColor: colors.surface, color: colors.text }} />
            </div>
            <div>
              <Label style={{ color: colors.text }}>Description</Label>
              <Textarea value={uploadForm.description} onChange={(e) => setUploadForm({...uploadForm, description: e.target.value})} rows={2} style={{ backgroundColor: colors.surface, color: colors.text }} />
            </div>

            {/* Upload mode toggle */}
            <div className="flex gap-2 p-1 rounded-lg" style={{ backgroundColor: colors.surface }}>
              <button onClick={() => setUploadMode('file')} className={`flex-1 py-2 px-3 rounded-md text-sm font-semibold transition-all ${uploadMode === 'file' ? 'bg-blue-600 text-white' : ''}`} style={uploadMode !== 'file' ? { color: colors.text } : {}}>
                <Upload className="w-4 h-4 inline mr-1" /> Upload File
              </button>
              <button onClick={() => setUploadMode('link')} className={`flex-1 py-2 px-3 rounded-md text-sm font-semibold transition-all ${uploadMode === 'link' ? 'bg-blue-600 text-white' : ''}`} style={uploadMode !== 'link' ? { color: colors.text } : {}}>
                <ExternalLink className="w-4 h-4 inline mr-1" /> Add Link
              </button>
            </div>

            {uploadMode === 'file' ? (
              <div>
                <Label style={{ color: colors.text }}>File *</Label>
                <div className="mt-1 border-2 border-dashed rounded-xl p-6 text-center cursor-pointer hover:bg-gray-50 transition-colors" style={{ borderColor: colors.border }}
                  onClick={() => document.getElementById('resource-file-input').click()}>
                  {selectedFile ? (
                    <div>
                      <FileText className="w-8 h-8 mx-auto mb-2 text-blue-500" />
                      <p className="text-sm font-semibold" style={{ color: colors.text }}>{selectedFile.name}</p>
                      <p className="text-xs text-gray-500">{(selectedFile.size / 1024 / 1024).toFixed(2)} MB</p>
                    </div>
                  ) : (
                    <div>
                      <Upload className="w-8 h-8 mx-auto mb-2 text-gray-400" />
                      <p className="text-sm" style={{ color: colors.text }}>Click to select a file</p>
                      <p className="text-xs text-gray-500">PDF, Word, Images (max 10MB)</p>
                    </div>
                  )}
                </div>
                <input id="resource-file-input" type="file" className="hidden" accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.png,.jpg,.jpeg,.gif" 
                  onChange={(e) => { if (e.target.files[0]) { if (e.target.files[0].size > 10 * 1024 * 1024) { toast.error('File too large (max 10MB)'); return; } setSelectedFile(e.target.files[0]); }}} />
              </div>
            ) : (
              <div>
                <Label style={{ color: colors.text }}>URL *</Label>
                <Input value={uploadForm.url} onChange={(e) => setUploadForm({...uploadForm, url: e.target.value})} placeholder="https://... (YouTube, Google Drive, website)" type="url" style={{ backgroundColor: colors.surface, color: colors.text }} />
              </div>
            )}

            <div>
              <Label style={{ color: colors.text }}>Category</Label>
              <select value={uploadForm.category} onChange={(e) => setUploadForm({...uploadForm, category: e.target.value})} className="w-full p-2 rounded border" style={{ backgroundColor: colors.surface, color: colors.text }}>
                {CATEGORIES.map(cat => (<option key={cat.id} value={cat.id}>{cat.emoji} {cat.name}</option>))}
              </select>
            </div>
            <div className="flex items-center gap-2">
              <Switch checked={uploadForm.is_featured} onCheckedChange={(checked) => setUploadForm({...uploadForm, is_featured: checked})} />
              <Label style={{ color: colors.text }}>Feature this document</Label>
            </div>
            <Button onClick={(e) => { e.preventDefault(); e.stopPropagation(); uploadResource(); }} disabled={uploading} className="w-full bg-blue-600 text-white" type="button">
              {uploading ? 'Uploading...' : <><Upload className="w-4 h-4 mr-2" />Add Resource</>}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ResourceManagement;
